import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { useIsAdmin } from '../auth/hooks';
import { HybridIcon } from '../components/HybridIcon';
import './Calendar.css';

type DayColumn = {
  label: string;
  date: Date;
};

function startOfWeek(date: Date): Date {
  const next = new Date(date);
  const day = next.getDay();
  const diff = next.getDate() - day;
  next.setDate(diff);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatShort(date: Date) {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatTime(date: Date | null) {
  if (!date) {
    return 'All day';
  }

  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export default function Calendar() {
  const { state } = useAppContext();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [selectedCar, setSelectedCar] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');

  const carMap = useMemo(() => {
    return new Map(state.cars.map((car) => [car.id, `${car.year} ${car.make} ${car.model}`]));
  }, [state.cars]);

  const customerMap = useMemo(() => {
    return new Map(state.customers.map((customer) => [customer.id, customer.name]));
  }, [state.customers]);

  const days: DayColumn[] = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) => {
      const date = addDays(weekStart, index);
      return {
        label: date.toLocaleDateString(undefined, { weekday: 'short' }),
        date,
      };
    });
  }, [weekStart]);

  const filteredRentals = useMemo(() => {
    return state.rentals.filter((rental) => {
      const carMatch = selectedCar === 'all' || rental.carId === selectedCar;
      const customerMatch = selectedCustomer === 'all' || rental.customerId === selectedCustomer;
      return carMatch && customerMatch;
    });
  }, [selectedCar, selectedCustomer, state.rentals]);

  const eventsByDay = useMemo(() => {
    return days.map((day) => {
      const start = new Date(day.date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(day.date);
      end.setHours(23, 59, 59, 999);

      const rentals = filteredRentals.filter((rental) => {
        const rentalStart = new Date(rental.startDate);
        const rentalEnd = rental.endDate ? new Date(rental.endDate) : rentalStart;
        return rentalStart <= end && rentalEnd >= start;
      });

      return { day, rentals };
    });
  }, [days, filteredRentals]);

  return (
    <div className="calendar-page">
      <section className="calendar-hero card">
        <div>
          <p className="eyebrow">Weekly Planner</p>
          <h1 className="calendar-title">Calendar</h1>
          <p className="calendar-subtitle">
            Week of {formatShort(weekStart)} to {formatShort(addDays(weekStart, 6))}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/rentals/new')}>
          <HybridIcon name="add-circle" size={18} color="currentColor" />
          {isAdmin ? 'Book for Client' : 'New Booking'}
        </button>
      </section>

      <section className="calendar-controls card">
        <div className="segment">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} type="button">Prev Week</button>
          <button className="is-active" onClick={() => setWeekStart(startOfWeek(new Date()))} type="button">Today</button>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} type="button">Next Week</button>
        </div>

        <div className="calendar-filter-row">
          <label className="field">
            <span>Vehicle</span>
            <select value={selectedCar} onChange={(event) => setSelectedCar(event.target.value)}>
              <option value="all">All vehicles</option>
              {state.cars.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.make} {car.model} ({car.licensePlate})
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Customer</span>
            <select value={selectedCustomer} onChange={(event) => setSelectedCustomer(event.target.value)}>
              <option value="all">All customers</option>
              {state.customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="calendar-grid">
        {eventsByDay.map(({ day, rentals }) => (
          <article key={day.date.toISOString()} className="calendar-day card">
            <header className="calendar-day-header">
              <strong>{day.label}</strong>
              <span>{formatShort(day.date)}</span>
            </header>

            {rentals.length === 0 ? (
              <div className="calendar-empty">No bookings</div>
            ) : (
              rentals.map((rental) => {
                const start = new Date(rental.startDate);
                const end = rental.endDate ? new Date(rental.endDate) : null;

                return (
                  <button key={rental.id} className="calendar-event" onClick={() => navigate(`/rentals/${rental.id}`)} type="button">
                    <div className="calendar-event-title">{carMap.get(rental.carId) || 'Unknown vehicle'}</div>
                    <div className="calendar-event-line">{customerMap.get(rental.customerId) || 'Unknown customer'}</div>
                    <div className="calendar-event-line">{formatTime(start)} to {formatTime(end)}</div>
                    {rental.createdByRole && (
                      <span className="pill pill-accent calendar-role">Booked by {rental.createdByRole}</span>
                    )}
                  </button>
                );
              })
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
